import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Input, Select, Textarea } from '@artosera/ui';
import { typeRdvCreationSchema, type TypeRdvEntree } from '@artosera/shared';
import type { TypeRdv } from '../../api/priseRendezVous.js';
import type { MembreEquipe } from '../../api/equipe.js';
import { PlagesEditeur } from './PlagesEditeur.js';
import styles from './RendezVous.module.css';

export function TypeFormulaire({
  type,
  membres,
  pending,
  onSauver,
  onFermer,
}: {
  type?: TypeRdv;
  membres: readonly MembreEquipe[];
  pending: boolean;
  onSauver: (corps: TypeRdvEntree) => void;
  onFermer: () => void;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const [valeurs, setValeurs] = useState<TypeRdvEntree>(() => ({
    slug: type?.slug ?? '',
    titre: type?.titre ?? { fr: '', en: '' },
    description: type?.description ?? { fr: '', en: '' },
    dureeMinutes: type?.dureeMinutes ?? 60,
    tamponAvantMinutes: type?.tamponAvantMinutes ?? 0,
    tamponApresMinutes: type?.tamponApresMinutes ?? 0,
    pasMinutes: type?.pasMinutes ?? 30,
    preavisHeures: type?.preavisHeures ?? 24,
    horizonJours: type?.horizonJours ?? 60,
    plages: type?.plages ?? [],
    membres: type?.membres ?? [],
    validation: type?.validation ?? 'manuelle',
    lieu: type?.lieu ?? '',
    oeuvre: type?.oeuvre ?? 'aucune',
    telephoneObligatoire: type?.telephoneObligatoire ?? false,
    enLigne: type?.enLigne ?? false,
    ordre: type?.ordre ?? 0,
  }));
  const [invalide, setInvalide] = useState(false);
  function monter(index: number, delta: number) {
    const ids = [...valeurs.membres];
    [ids[index], ids[index + delta]] = [ids[index + delta]!, ids[index]!];
    setValeurs({ ...valeurs, membres: ids });
  }
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        const resultat = typeRdvCreationSchema.safeParse(valeurs);
        setInvalide(!resultat.success);
        if (resultat.success) onSauver(resultat.data);
      }}
    >
      <fieldset className={styles.bloc} disabled={pending}>
        <legend>
          {t(type ? 'regie:priseRendezVous.modifierType' : 'regie:priseRendezVous.nouveauType')}
        </legend>
        <div className={styles.grille}>
          <label className={styles.champ}>
            {t('regie:priseRendezVous.slug')}
            <Input
              required
              maxLength={120}
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              value={valeurs.slug}
              onChange={(e) => setValeurs({ ...valeurs, slug: e.target.value })}
            />
          </label>
          {(['fr', 'en'] as const).map((langue) => (
            <label key={langue} className={styles.champ}>
              {t(`regie:priseRendezVous.titreType.${langue}`)}
              <Input
                required={langue === 'fr'}
                maxLength={200}
                value={valeurs.titre[langue]}
                onChange={(e) =>
                  setValeurs({ ...valeurs, titre: { ...valeurs.titre, [langue]: e.target.value } })
                }
              />
            </label>
          ))}
          {(['fr', 'en'] as const).map((langue) => (
            <label key={langue} className={styles.champ}>
              {t(`regie:priseRendezVous.description.${langue}`)}
              <Textarea
                maxLength={1000}
                value={valeurs.description[langue]}
                onChange={(e) =>
                  setValeurs({
                    ...valeurs,
                    description: { ...valeurs.description, [langue]: e.target.value },
                  })
                }
              />
            </label>
          ))}
          {(
            [
              ['dureeMinutes', 15, 480, 5],
              ['tamponAvantMinutes', 0, 120, 1],
              ['tamponApresMinutes', 0, 120, 1],
              ['preavisHeures', 0, 720, 'any'],
              ['horizonJours', 1, 180, 1],
              ['ordre', 0, undefined, 1],
            ] as const
          ).map(([cle, min, max, step]) => (
            <label key={cle} className={styles.champ}>
              {t(`regie:priseRendezVous.${cle}`)}
              <Input
                type="number"
                required
                min={min}
                max={max}
                step={step}
                value={valeurs[cle]}
                onChange={(e) => setValeurs({ ...valeurs, [cle]: e.target.valueAsNumber })}
              />
            </label>
          ))}
          <label className={styles.champ}>
            {t('regie:priseRendezVous.pasMinutes')}
            <Select
              value={String(valeurs.pasMinutes)}
              onValueChange={(v) =>
                setValeurs({ ...valeurs, pasMinutes: Number(v) as 15 | 30 | 60 })
              }
              options={[15, 30, 60].map((n) => ({ value: String(n), label: String(n) }))}
            />
          </label>
          <label className={styles.champ}>
            {t('regie:priseRendezVous.validation')}
            <Select
              value={valeurs.validation}
              onValueChange={(v) =>
                setValeurs({ ...valeurs, validation: v as TypeRdvEntree['validation'] })
              }
              options={['manuelle', 'automatique'].map((v) => ({
                value: v,
                label: t(`regie:priseRendezVous.${v}`),
              }))}
            />
          </label>
          <label className={styles.champ}>
            {t('regie:priseRendezVous.lieu')}
            <Input
              maxLength={500}
              value={valeurs.lieu}
              onChange={(e) => setValeurs({ ...valeurs, lieu: e.target.value })}
            />
          </label>
          <label className={styles.champ}>
            {t('regie:priseRendezVous.oeuvre')}
            <Select
              value={valeurs.oeuvre}
              onValueChange={(v) =>
                setValeurs({ ...valeurs, oeuvre: v as TypeRdvEntree['oeuvre'] })
              }
              options={['aucune', 'facultative'].map((v) => ({
                value: v,
                label: t(`regie:priseRendezVous.${v}`),
              }))}
            />
          </label>
        </div>
        <Checkbox
          label={t('regie:priseRendezVous.telephoneObligatoire')}
          checked={valeurs.telephoneObligatoire}
          onCheckedChange={(v) => setValeurs({ ...valeurs, telephoneObligatoire: v === true })}
        />
        <Checkbox
          label={t('regie:priseRendezVous.enLigne')}
          checked={valeurs.enLigne}
          onCheckedChange={(v) => setValeurs({ ...valeurs, enLigne: v === true })}
        />
        <PlagesEditeur
          plages={valeurs.plages}
          onChange={(plages) => setValeurs({ ...valeurs, plages })}
        />
        <h3>{t('regie:priseRendezVous.membres')}</h3>
        <p>{t('regie:priseRendezVous.membresAide')}</p>
        <ol>
          {valeurs.membres.map((id, index) => {
            const nom = membres.find((m) => m.compteId === id)?.nom ?? id;
            return (
              <li key={id}>
                <div className={styles.actions}>
                  <span>{nom}</span>
                  <Button
                    variant="secondary"
                    disabled={index === 0}
                    aria-label={t('regie:priseRendezVous.monterMembre', { nom })}
                    onClick={() => monter(index, -1)}
                  >
                    {t('regie:priseRendezVous.monter')}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={index === valeurs.membres.length - 1}
                    aria-label={t('regie:priseRendezVous.descendreMembre', { nom })}
                    onClick={() => monter(index, 1)}
                  >
                    {t('regie:priseRendezVous.descendre')}
                  </Button>
                  <Button
                    variant="ghost"
                    aria-label={t('regie:priseRendezVous.retirerMembre', { nom })}
                    onClick={() =>
                      setValeurs({ ...valeurs, membres: valeurs.membres.filter((v) => v !== id) })
                    }
                  >
                    {t('regie:priseRendezVous.retirer')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ol>
        <label className={styles.champ}>
          {t('regie:priseRendezVous.ajouterMembre')}
          <Select
            value=""
            disabled={valeurs.membres.length >= 10}
            placeholder={t('regie:priseRendezVous.choisirMembre')}
            onValueChange={(id) => setValeurs({ ...valeurs, membres: [...valeurs.membres, id] })}
            options={membres
              .filter((m) => !valeurs.membres.includes(m.compteId))
              .map((m) => ({ value: m.compteId, label: m.nom }))}
          />
        </label>
      </fieldset>
      {invalide ? <p role="alert">{t('regie:priseRendezVous.typeInvalide')}</p> : null}
      <div className={styles.actions}>
        <Button type="submit" loading={pending}>
          {t('commun:actions.enregistrer')}
        </Button>
        <Button variant="ghost" disabled={pending} onClick={onFermer}>
          {t('commun:actions.annuler')}
        </Button>
      </div>
    </form>
  );
}
